
// src/components/auth/AuthForm.js
import React, { useEffect, useState } from 'react';
import { requestOtp, verifyOtp, checkUserByEmail } from '../../api/auth/authApi';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { IoIosMailUnread } from "react-icons/io";
import { FaUserLarge } from 'react-icons/fa6';

const AuthForm = () => {
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isSending, setIsSending] = useState(false); // state for disabling button
  const [resendCount, setResendCount] = useState(0); // state for tracking resend attempts
  const navigate = useNavigate();
  const [isNameRequired, setIsNameRequired] = useState(false);
  const [isNameEntered, setIsNameEntered] = useState(false);

  useEffect(() => {
    let countdown;
    if (timer > 0 && !canResend) {
      countdown = setTimeout(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(countdown);
  }, [timer, canResend]);

  const handleRequestOtp = async () => {
    setIsSending(true); // disable button
    try {
      if (isNameEntered) {
      await requestOtp(email, fullName);
      alert('OTP sent successfully.');
      setStep(2);
      setTimer(30);
      setCanResend(false);
      setResendCount(1); // initialize resend count to 1 after the first send
    }
    else {
      const user = await checkUserByEmail(email);
      if (user) {
        await requestOtp(email, fullName);
        alert('OTP sent successfully.');
        setStep(2);
        setTimer(30);
        setCanResend(false);
        setResendCount(1);
      }
      else {
        setIsNameRequired(true);
        alert('Email Not Registered. Please Enter your full name to proceed.');
      }
    }
    } catch (error) {
      alert('Failed to send OTP. Please try again.');
    } finally {
      setIsSending(false); // re-enable button in case of an error
    }
  };

  const handleResendOtp = async () => {
    if (resendCount >= 3) {
      alert('Maximum OTP request attempts expired.');
      return;
    }
    try {
      await requestOtp(email, fullName);
      alert('OTP resent successfully.');
      setTimer(30); // Reset countdown after resending
      setCanResend(false); // Disable resend button again
      setResendCount((prev) => prev + 1); // Increment resend count
    } catch (error) {
      alert('Failed to resend OTP. Please try again.');
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const response = await verifyOtp(email, otp, fullName);
      if (response.message === 'OTP verified successfully.') {
        const { token } = response;
        login({ fullName, email }, token);
        alert('OTP verified successfully!');
        navigate('/dashboard', { state: { showPopup: true } });
      } else {
        alert('Invalid OTP. Please try again.');
      }
    } catch (error) {
      alert('Invalid OTP. Please try again.');
    }
  };

  return step === 1 ? (
    <div className="w-9/12 mx-auto">
      <div className="">
        <h5 className="text-left md:font-medium font-bold text-xs md:text-lg">Enter Email</h5>
        <div className='flex justify-end items-end md:border-b-4 border-b-2 border-b-[#6C63FF]'>
        <input
          type="email"
          className="w-full md:h-10 h-6 pr-2 text-xs md:text-lg focus:outline-none"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <IoIosMailUnread aria-label='Mail' className='md:text-2xl text-xl text-[#6C63FF]'/>

        </div>
        
      </div>
      {isNameRequired && (
        <div className="py-5">
          <h5 className="text-left">Enter Full Name</h5>
        <div className='flex justify-end items-end md:border-b-4 border-b-2 border-b-[#6C63FF]'>
          <input
            type="text"
            className="w-full md:h-10 h-6 pr-2 text-xs md:text-lg focus:outline-none"
            placeholder="Full Name"
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setIsNameEntered(true); // Once full name is entered, set this flag to true
            }}
          />
          <FaUserLarge aria-label='User' className='md:text-2xl text-xl text-[#6C63FF]'/>
        </div>
      </div>
      )}
      <div className="md:pb-8 pb-3 pt-1">
        <p className="text-right font-medium text-xs md:text-base">Resend OTP?</p>
      </div>

      <button
        className="w-[50%] bg-[#6C63FF] rounded-full py-2 text-white font-semibold md:mb-16 mb-6 text-sm md:text-lg"
        onClick={handleRequestOtp}
        disabled={isSending} // disable button when OTP is being sent
      >
        {isSending ? 'Sending...' : 'Send OTP'}
      </button>
    </div>
  ) : (
    <div className='w-9/12 mx-auto md:pt-5 pt-2'>
        <h5 className="text-left md:font-medium font-bold text-xs md:text-lg">Enter OTP</h5>
      <div className='md:pb-12 pb-3'>
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderInput={(props) => (
            <input
              {...props} style={{boxShadow: "inset -6px 6px 5px rgba(0,0,0,0.25)" }}
              className="md:rounded-xl rounded-lg text-center ml-1 w-full md:h-9 h-8  bg-[#d9d9d9] focus:outline-none"
            />
          )}
        />
      </div>

      <button
        className="w-[50%] bg-[#6C63FF] rounded-full py-2 text-white font-semibold md:mb-16 text-sm md:text-lg"
        onClick={handleVerifyOtp}
      >
        Verify OTP
      </button>

      <div className="md:pt-3 pb-6 text-center">
        {canResend ? (
          resendCount < 3 ? (
            <button className="text-blue-500 text-xs md:text-sm underline" onClick={handleResendOtp}>
              Resend OTP?
            </button>
          ) : (
            <p className="text-red-500">Maximum OTP request attempts expired</p>
          )
        ) : (
          <p>Resend OTP in {timer} seconds</p>
        )}
      </div>
    </div>
  );
};

export default AuthForm;